import { useState } from "react";
import { IntensityIndicator } from "@/components/shared/IntensityIndicator";
import { Button } from "@/components/ui/button";
import { type BadgeStatus, StatusBadge } from "@/components/ui/status-badge";
import {
  isOffPodBasis,
  type SignalListRow,
  type SignalType,
  useApplyPodChange,
  useConfirmIntensity,
  useConfirmIntensityAndPodChange,
  useDismissSignal,
} from "@/hooks/use-signals";
import { useSession } from "@/lib/auth-client";
import { AddResourceDialog } from "./AddResourceDialog";
import { OffPodBasis, SuggestionBasis } from "./SuggestionBasis";

const TYPE_LABEL: Record<SignalType, string> = {
  needs_guidance: "Needs Guidance",
  guidance_change_pod_change: "Pod Change",
  intensity_drift: "Intensity Drift",
  resource_off_pod: "Add Resource",
};

const TYPE_STATUS: Record<SignalType, BadgeStatus> = {
  needs_guidance: "pending",
  guidance_change_pod_change: "info",
  intensity_drift: "failed",
  resource_off_pod: "brand",
};

/** Coarse "Nd" / "Nh" age label — the queue has no fine-grained SLA display need. */
function formatAge(ageMs: number): string {
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 60) return `${Math.max(minutes, 0)}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

interface Props {
  signal: SignalListRow;
  /** Drop the Solution · Account line — set when embedded in that Solution's own drawer. */
  hideSolutionHeader?: boolean;
  /** When set (and the header is shown), the Solution name becomes a drill-in button. */
  onOpenSolution?: (solutionId: string) => void;
}

export function SignalRow({ signal, hideSolutionHeader = false, onOpenSolution }: Props) {
  const { data: session } = useSession();
  // §4: CEO sees the Signals hub read-only. Only RM holds confirmSuggestedIntensity /
  // applySuggestedPodChange / dismissSignal — hide the write actions entirely for
  // everyone else so a CEO can't click into a 403 (mirrors DeleteSolutionButton in
  // SolutionDetailDrawer.tsx).
  const isRm = session?.user?.role === "RM";
  const [addOpen, setAddOpen] = useState(false);
  const confirmIntensity = useConfirmIntensity();
  const applyPodChange = useApplyPodChange();
  const confirmBoth = useConfirmIntensityAndPodChange();
  const dismissSignal = useDismissSignal();

  const isOffPod = signal.type === "resource_off_pod";
  // A half is actionable only while its suggestion differs from current — applying it
  // advances current to match, which hides that button. Mirrors isIntensityActionable /
  // isPodActionable in src/api/signals.ts so the UI and server agree on what's pending.
  const intensityActionable =
    signal.suggestedIntensity !== null && signal.suggestedIntensity !== signal.currentIntensity;
  const hasPodChange =
    signal.type === "guidance_change_pod_change" &&
    signal.suggestedPodSize !== null &&
    signal.suggestedPodSize !== signal.currentPodSize;

  return (
    <li className="flex flex-col gap-2 rounded-lg border p-3 md:flex-row md:items-start md:justify-between">
      <div className="flex flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          {!hideSolutionHeader && (
            <>
              {onOpenSolution ? (
                <button
                  type="button"
                  onClick={() => onOpenSolution(signal.solutionId)}
                  className="font-medium text-foreground underline-offset-2 hover:underline"
                >
                  {signal.solutionName}
                </button>
              ) : (
                <span className="font-medium">{signal.solutionName}</span>
              )}
              <span className="text-muted-foreground">·</span>
              <span className="text-sm text-muted-foreground">{signal.accountName}</span>
            </>
          )}
          <StatusBadge status={TYPE_STATUS[signal.type]}>{TYPE_LABEL[signal.type]}</StatusBadge>
          <span className="ml-auto text-xs text-muted-foreground md:ml-0">
            {formatAge(signal.ageMs)} ago
          </span>
        </div>

        {isOffPod ? (
          <div className="text-sm">
            Add{" "}
            <span className="font-medium text-foreground">
              {signal.suggestedResourceName ?? "this Resource"}
            </span>{" "}
            to the Pod
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="flex items-center gap-1.5">
              {signal.currentIntensity !== null ? (
                <IntensityIndicator level={signal.currentIntensity} size="sm" />
              ) : (
                <span className="text-muted-foreground">no Intensity set</span>
              )}
              {/* Show the "→ suggested" only while the Intensity half is still pending. */}
              {intensityActionable && signal.suggestedIntensity !== null && (
                <>
                  <span className="text-muted-foreground">→</span>
                  <IntensityIndicator level={signal.suggestedIntensity} size="sm" />
                </>
              )}
            </span>
            {hasPodChange && signal.suggestedPodSize !== null && (
              <span className="text-muted-foreground">
                {signal.currentPodSize} → {signal.suggestedPodSize}
                {signal.suggestedSupportingPoints
                  ? ` (+${signal.suggestedSupportingPoints} Supporting)`
                  : ""}
              </span>
            )}
          </div>
        )}

        {isOffPodBasis(signal.basis) ? (
          <OffPodBasis basis={signal.basis} />
        ) : (
          <SuggestionBasis basis={signal.basis} />
        )}
      </div>

      {isRm && (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {isOffPod ? (
            <Button type="button" size="sm" variant="outline" onClick={() => setAddOpen(true)}>
              Add Resource
            </Button>
          ) : (
            <>
              {intensityActionable && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={confirmIntensity.isPending}
                  onClick={() => confirmIntensity.mutate(signal.id)}
                >
                  Confirm Intensity
                </Button>
              )}
              {hasPodChange && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={applyPodChange.isPending}
                  onClick={() => applyPodChange.mutate(signal.id)}
                >
                  Apply Pod change
                </Button>
              )}
              {intensityActionable && hasPodChange && (
                <Button
                  type="button"
                  size="sm"
                  disabled={confirmBoth.isPending}
                  onClick={() => confirmBoth.mutate(signal.id)}
                >
                  Confirm Intensity &amp; Pod
                </Button>
              )}
            </>
          )}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={dismissSignal.isPending}
            onClick={() => dismissSignal.mutate(signal.id)}
          >
            Dismiss
          </Button>
        </div>
      )}

      {isRm && isOffPod && (
        <AddResourceDialog signal={signal} open={addOpen} onOpenChange={setAddOpen} />
      )}
    </li>
  );
}
